const MAX_GRAPH_JSON_LENGTH = 100000;
const MAX_GRAPH_TRACES = 20;
const MAX_GRAPH_POINTS = 10000;
const MAX_GRAPH_STRING_LENGTH = 2000;

const GRAPH_BEGIN = '[[GRAPH_JSON_BEGIN]]';
const GRAPH_END = '[[GRAPH_JSON_END]]';
const CODE_BLOCK_PATTERN = /```([^\r\n]*)\r?\n([\s\S]*?)```/g;

const GRAPH_DATA_KEYS = new Set([
    'type', 'x', 'y', 'z', 'lat', 'lon', 'mode', 'name', 'text', 'marker',
    'line', 'colorscale', 'showscale', 'hovertemplate',
]);
const GRAPH_LAYOUT_KEYS = new Set([
    'title', 'xaxis', 'yaxis', 'zaxis', 'width', 'height', 'margin', 'showlegend',
]);
const GRAPH_CONFIG_KEYS = new Set([
    'displayModeBar', 'responsive', 'staticPlot', 'scrollZoom', 'displaylogo',
    'modeBarButtonsToRemove',
]);

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const cloneSafeValue = (value, depth = 0) => {
    if (depth > 6) {
        return undefined;
    }
    if (typeof value === 'string') {
        return value.slice(0, MAX_GRAPH_STRING_LENGTH);
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    if (Array.isArray(value)) {
        if (value.length > MAX_GRAPH_POINTS) {
            return undefined;
        }
        return value.map((item) => cloneSafeValue(item, depth + 1)).filter((item) => item !== undefined);
    }
    if (isPlainObject(value)) {
        return Object.entries(value).reduce((result, [key, item]) => {
            if (/^(on|src|href)/i.test(key)) {
                return result;
            }
            const safeItem = cloneSafeValue(item, depth + 1);
            if (safeItem !== undefined) {
                result[key] = safeItem;
            }
            return result;
        }, {});
    }
    return undefined;
};

const pickSafeObject = (value, allowedKeys) => {
    if (!isPlainObject(value)) {
        return {};
    }
    return Array.from(allowedKeys).reduce((result, key) => {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
            const safeValue = cloneSafeValue(value[key]);
            if (safeValue !== undefined) {
                result[key] = safeValue;
            }
        }
        return result;
    }, {});
};

export const safeGraphSpec = (value) => {
    if (
        !isPlainObject(value)
        || !Array.isArray(value.data)
        || value.data.length === 0
        || value.data.length > MAX_GRAPH_TRACES
        || !value.data.every((trace) => isPlainObject(trace))
    ) {
        return null;
    }
    return {
        data: value.data.map((trace) => pickSafeObject(trace, GRAPH_DATA_KEYS)),
        layout: pickSafeObject(value.layout, GRAPH_LAYOUT_KEYS),
        config: pickSafeObject(value.config, GRAPH_CONFIG_KEYS),
    };
};

const readBalancedJson = (value, start) => {
    const opening = value[start];
    const closing = opening === '{' ? '}' : '[';
    if (!['{', '['].includes(opening)) {
        return null;
    }

    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let index = start; index < value.length; index += 1) {
        const character = value[index];
        if (inString) {
            if (escaped) {
                escaped = false;
            } else if (character === '\\') {
                escaped = true;
            } else if (character === '"') {
                inString = false;
            }
            continue;
        }
        if (character === '"') {
            inString = true;
        } else if (character === opening) {
            depth += 1;
        } else if (character === (opening === '{' ? '}' : ']')) {
            depth -= 1;
            if (depth === 0) {
                return { end: index + 1, json: value.slice(start, index + 1) };
            }
        }
    }
    return null;
};

const parseGraphAt = (value, start, markerLength) => {
    const jsonStart = start + markerLength;
    const balanced = readBalancedJson(value, jsonStart);
    if (!balanced || balanced.json.length > MAX_GRAPH_JSON_LENGTH) {
        return null;
    }
    if (value[balanced.end] !== ']') {
        return null;
    }
    try {
        const spec = safeGraphSpec(JSON.parse(balanced.json));
        return spec ? { end: balanced.end + 1, spec } : null;
    } catch (error) {
        return null;
    }
};

const parseGraphMarker = (value, start) => {
    if (value.startsWith(GRAPH_BEGIN, start)) {
        const jsonStart = start + GRAPH_BEGIN.length;
        const endMarker = value.indexOf(GRAPH_END, jsonStart);
        if (endMarker === -1 || endMarker - jsonStart > MAX_GRAPH_JSON_LENGTH) {
            return null;
        }
        try {
            const spec = safeGraphSpec(JSON.parse(value.slice(jsonStart, endMarker).trim()));
            return spec ? { end: endMarker + GRAPH_END.length, spec } : null;
        } catch (error) {
            return null;
        }
    }
    if (value.startsWith('[GRAPH:', start)) {
        return parseGraphAt(value, start, '[GRAPH:'.length);
    }
    return null;
};

const normaliseLanguage = (info) => {
    const language = info.trim().split(/\s+/)[0].toLowerCase();
    return /^[a-z0-9+#._-]{1,32}$/.test(language) ? language : 'text';
};

export const parseAgentContent = (content) => {
    const value = typeof content === 'string' ? content : '';
    const tokens = [];
    let cursor = 0;
    const pushText = (text) => {
        if (text) {
            tokens.push({ type: 'text', value: text });
        }
    };

    while (cursor < value.length) {
        CODE_BLOCK_PATTERN.lastIndex = cursor;
        const codeMatch = CODE_BLOCK_PATTERN.exec(value);
        const graphIndex = value.indexOf('[[GRAPH_JSON_BEGIN]]', cursor);
        const legacyGraphIndex = value.indexOf('[GRAPH:', cursor);
        const candidates = [
            codeMatch ? { index: codeMatch.index, kind: 'code', match: codeMatch } : null,
            graphIndex === -1 ? null : { index: graphIndex, kind: 'graph' },
            legacyGraphIndex === -1 ? null : { index: legacyGraphIndex, kind: 'graph' },
        ].filter(Boolean).sort((left, right) => left.index - right.index);
        const next = candidates[0];
        if (!next) {
            pushText(value.slice(cursor));
            break;
        }
        pushText(value.slice(cursor, next.index));
        if (next.kind === 'code') {
            tokens.push({
                type: 'code',
                language: normaliseLanguage(next.match[1]),
                value: next.match[2],
            });
            cursor = CODE_BLOCK_PATTERN.lastIndex;
            continue;
        }
        const graph = parseGraphMarker(value, next.index);
        if (!graph) {
            tokens.push({ type: 'text', value: value.slice(next.index, next.index + 1) });
            cursor = next.index + 1;
            continue;
        }
        tokens.push({ type: 'graph', spec: graph.spec });
        cursor = graph.end;
    }
    return tokens;
};

export { GRAPH_BEGIN, GRAPH_END };
